"use client";

import { track } from "@homeinstead/analytics";
import { EventName } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import { Card, LinkCard } from "@homeinstead/ui/components/Card";
import Image from "next/image";
import { BlockDebugDev } from "~www/components/PageBuilder/BlockDebugDev";

interface ServiceCardProps {
  serviceName: string;
  description: string;
  iconUrl?: string;
  href?: string;
}

const ServiceCard = ({
  serviceName,
  description,
  iconUrl,
  href,
}: ServiceCardProps) => {
  const hasIcon = Boolean(iconUrl);
  const CardComponent = href ? LinkCard : Card;

  const handleLinkClick = () => {
    if (href) {
      track(EventName.TRACKED_CLICK, {
        element_name: "service_card_link",
        target_url: href,
      });
    }
  };

  if (!hasIcon) {
    return (
      <BlockDebugDev
        blockType="ServiceCard"
        message={`Missing service icon for: "${serviceName}"`}
      />
    );
  }

  return (
    <li className="list-none">
      <CardComponent
        {...(href && {
          "aria-label": `Learn more about ${serviceName}`,
          href,
          onClick: handleLinkClick,
        })}
        className={
          "flex h-full min-h-[140px] flex-col gap-4 rounded-lg bg-card bg-white p-6 shadow-md sm:flex-row"
        }
      >
        <div aria-hidden="true" className="flex-shrink-0">
          <Image
            alt=""
            className="h-16 w-16 object-contain"
            data-testid="service-icon"
            height={64}
            src={iconUrl}
            width={64}
          />
        </div>
        <div className="flex min-w-0 flex-grow flex-col">
          <h3
            className={cn("font-bold text-md text-new-beginnings-base", {
              underline: Boolean(href),
            })}
          >
            {serviceName}
          </h3>
          <p className="mt-1 line-clamp-4 text-new-beginnings-base">
            {description}
          </p>
        </div>
      </CardComponent>
    </li>
  );
};

export default ServiceCard;
